import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Typography,
  DatePicker,
  Button,
  Space,
  Table,
  message,
  Row,
  Col,
  Input,
  Select,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../configs";
import { getUser } from "../../utils/DataUser";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const DOC_OPTIONS = [
  { label: "Semua", value: null },
  { label: "BC 1.6", value: 16 },
  { label: "BC 2.7", value: 27 },
  { label: "BC 4.0", value: 40 },
];

export default function LaporanPemasukanPerDokPabean() {
  const [range, setRange] = useState([]);
  const [idDocument, setIdDocument] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchData = useCallback(
    async (page, pageSize, idDocument, dateRange, search = "") => {
      setLoading(true);
      try {
        const user = getUser();
        const userLevel = String(user?.id_user_level || user?.id_level || "");

        const params = {
          page,
          limit: pageSize,
        };

        if (dateRange && dateRange.length === 2) {
          params.start_date = dateRange[0].format("YYYY-MM-DD");
          params.end_date = dateRange[1].format("YYYY-MM-DD");
        }

        if (idDocument) {
          params.id_document = idDocument;
        }

        if (search && search.trim()) {
          params.search = search.trim();
        }

        if (userLevel === "7") {
          params.id_customer = user.username;
        }

        const res = await axiosInstance.get("/report/pemasukan-pabean", {
          params,
        });

        const fetchedData = res.data.data || [];

        setData(fetchedData);
        setPagination((prev) => ({
          ...prev,
          current: res.data.page,
          pageSize: res.data.limit,
          total: res.data.total,
        }));
      } catch (e) {
        console.error(e);
        message.error("Gagal load laporan pemasukan");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchData(1, 10, idDocument, range, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchData(1, pagination.pageSize, idDocument, range, searchText);
  };

  const handleReset = () => {
    setRange([]);
    setIdDocument(null);
    setSearchText("");
    fetchData(1, 10, null, [], "");
  };

  const handleTableChange = (newPagination) => {
    fetchData(
      newPagination.current,
      newPagination.pageSize,
      idDocument,
      range,
      searchText,
    );
  };

  const handleExport = async () => {
    try {
      const user = getUser();
      const userLevel = String(user?.id_user_level || user?.id_level || "");

      const params = { format: "xlsx" };

      if (range && range.length === 2) {
        params.start_date = range[0].format("YYYY-MM-DD");
        params.end_date = range[1].format("YYYY-MM-DD");
      }

      if (idDocument) {
        params.id_document = idDocument;
      }

      if (searchText && searchText.trim()) {
        params.search = searchText.trim();
      }

      if (userLevel === "7") {
        params.id_customer = user.username;
      }

      const response = await axiosInstance.get(
        "/report/pemasukan-pabean/export",
        {
          params,
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `laporan_pemasukan_pabean_${params.start_date || "all"}_${params.end_date || "all"}.xlsx`;
      link.click();
      message.success("Export berhasil");
    } catch (err) {
      console.error(err);
      message.error("Gagal mengunduh file excel");
    }
  };

  const columns = [
    {
      title: "No",
      key: "no",
      width: 50,
      align: "center",
      fixed: "left",
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Dok Pabean",
      children: [
        {
          title: "Jenis",
          dataIndex: "jenis_dokumen",
          width: 70,
          align: "center",
        },
        {
          title: "No. Daftar",
          dataIndex: "document_number",
          width: 80,
          ellipsis: true,
        },
        {
          title: "Tgl",
          dataIndex: "document_date",
          width: 85,
          render: (val) => (val ? dayjs(val).format("DD-MM-YY") : ""),
        },
      ],
    },
    {
      title: "BPB/GRN",
      children: [
        { title: "No", dataIndex: "bpb_number", width: 120, ellipsis: true },
        {
          title: "Tgl",
          dataIndex: "bpb_date",
          width: 85,
          render: (val) => (val ? dayjs(val).format("DD-MM-YY") : ""),
        },
      ],
    },
    {
      title: "Pengirim",
      dataIndex: "pengirim_barang",
      width: 120,
      ellipsis: true,
    },
    { title: "Pemilik", dataIndex: "nama_pemilik", width: 120, ellipsis: true },
    { title: "Kode", dataIndex: "item_code", width: 100, ellipsis: true },
    {
      title: "Nama Barang",
      dataIndex: "item_name",
      width: 160,
      ellipsis: true,
    },
    { title: "Sat", dataIndex: "satuan", width: 60, align: "center" },
    { title: "Jml", dataIndex: "jumlah", align: "right", width: 80 },
    {
      title: "Nilai",
      dataIndex: "nilai",
      align: "right",
      width: 130,
      render: (val) => (val ? Number(val).toLocaleString("id-ID") : ""),
    },
    { title: "Currency", dataIndex: "currency", align: "right", width: 70 },
  ];

  return (
    <Card bodyStyle={{ padding: "16px" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            Laporan Pemasukan Barang
          </Title>
          <Text type="secondary">Per Dokumen Pabean</Text>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExport}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
          >
            Export Excel
          </Button>
        </Col>
      </Row>

      <div
        style={{
          padding: "16px",
          background: "#ffffff",
          borderRadius: "8px",
          marginBottom: "24px",
          border: "1px solid #f0f0f0",
        }}
      >
        <Row gutter={[16, 16]} align="top">
          <Col xs={24} sm={12} lg={7}>
            <Input
              placeholder="Cari kode atau nama barang..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
            />
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Select
              placeholder="Pilih Dokumen Pabean"
              options={DOC_OPTIONS}
              onChange={(value) => setIdDocument(value)}
              style={{ width: "100%", height: "40px" }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} lg={7}>
            <RangePicker
              value={range}
              onChange={(val) => setRange(val || [])}
              format="DD-MM-YYYY"
              allowClear
              style={{ width: "100%", height: "40px" }}
            />
          </Col>
          <Col xs={24} sm={12} lg={5}>
            <Space
              style={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                Reset
              </Button>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                Cari
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Table
        size="small"
        bordered
        loading={loading}
        columns={columns}
        dataSource={data}
        rowKey={(record, index) =>
          `${record.id_incoming}-${record.item_code}-${index}`
        }
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          size: "small",
          showTotal: (total) => `Total ${total}`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
      />
    </Card>
  );
}
